import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Modal, Button, Form, Spinner, Table } from "react-bootstrap";
import html2pdf from "html2pdf.js";
import toast from "react-hot-toast";

/* ======================================================
   PRICING DATA
====================================================== */
const EDUALL_PRICING = {
  currency: "Kz",
  ivaIncluded: false,
  modules: [
    "Secretaria","Biblioteca","Chat / Gestor de tarefa",
    "Administração","Finanças","Transporte escolar",
    "Portal do Professor","Portal do Aluno",
    "Portal do Encarregado","Pedagogia",
    "Monitoramento / CCTV","Sistemas ERP"
  ],
  categories: {
    universities: {
      name: "Universidades e Institutos Superiores",
      plans: [
        { id:"uni_platina",  name:"Plano Platina",  price:399999, billing:"mensal", includedModules:"todos" },
        { id:"uni_diamante", name:"Plano Diamante", price:469999, billing:"mensal", includedModules:"todos" }
      ]
    },
    publicSchools: {
      name: "Escolas Públicas",
      plans: [
        { id:"pub_basico",   name:"Básico",   price:66999, billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefa","Administração","Finanças","Transporte escolar"] },
        { id:"pub_premium",  name:"Premium",  price:76999, billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefa","Administração","Finanças","Transporte escolar","Portal do Professor","Pedagogia","Monitoramento / CCTV"] },
        { id:"pub_moderado", name:"Moderado", price:2499,  billing:"anual_por_aluno", minimumStudents:350, includedModules:"todos" }
      ]
    },
    privateSchools: {
      name: "Colégios & Escolas Privadas",
      plans: [
        { id:"priv_basico",   name:"Básico",   price:99999,  billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefa","Administração","Finanças","Transporte escolar"] },
        { id:"priv_premium",  name:"Premium",  price:169999, billing:"mensal",
          includedModules:["Secretaria","Biblioteca","Chat / Gestor de tarefa","Administração","Finanças","Transporte escolar","Portal do Professor","Pedagogia"] },
        { id:"priv_avancado", name:"Avançado", price:249999, billing:"mensal", includedModules:"todos" }
      ]
    }
  },
  additionalServices: [
    { id:"sms",               name:"SMS Institucional",                     price:13.99,  billing:"por_sms" },
    { id:"pagamentos_online", name:"Pagamentos Online",                     price:79999,  billing:"mensal" },
    { id:"manutencao_anual",  name:"Manutenção / Formação / Suporte Anual", price:39999,  billing:"anual_unico" },
    { id:"leitor_rfid",       name:"Leitor RFID Portátil",                  price:25999,  billing:"pagamento_unico" },
    { id:"terminal_rfid",     name:"Terminal RFID Standalone",              price:99840,  billing:"pagamento_unico" }
  ]
};

const BILLING_MULTIPLIER = { trimestral:3, semestral:6, anual:12 };

/* ======================================================
   DEFAULT CUSTOM PRODUCTS (pré-definidos e editáveis)
====================================================== */
const DEFAULT_CUSTOM_ITEMS = [
  {
    id: 1,
    name: "Leitor USB para cartões RFID",
    price: 19999,
    qty: 1,
    imageUrl: "https://http2.mlstatic.com/D_721846-MLA105265768777_012026-C.jpg"
  },
  {
    id: 2,
    name: "Cartão de identificação e acesso codificado com tecnologia RFID (personalizado)",
    price: 3500,
    qty: 1,
    imageUrl: "https://ik.imagekit.io/zks5iegia/zebra-800059-304.jpg"
  }
];

/* ======================================================
   STYLES
====================================================== */
const styles = `
.pretty-modal .modal-dialog { max-width:900px; }
.pretty-modal .modal-content {
  border-radius:18px;
  border:1px solid rgba(226,232,240,.95);
  box-shadow:0 30px 90px rgba(2,6,23,.22);
}
.pretty-modal .modal-body {
  background:linear-gradient(180deg,rgba(248,250,252,.75),#fff 40%);
  max-height:560px;
  overflow-y:auto;
}
.pretty-modal .modal-body::-webkit-scrollbar { width:5px; }
.pretty-modal .modal-body::-webkit-scrollbar-thumb { background:#d1d5db; border-radius:99px; }
.cardlet {
  background:#fff;
  border:1px solid rgba(226,232,240,.95);
  border-radius:16px;
  padding:16px;
  margin-bottom:14px;
}
.cardlet-title {
  font-size:.82rem; font-weight:700; color:#475569;
  margin-bottom:12px; letter-spacing:.3px;
}
.price-box {
  border-radius:16px; border:1px solid rgba(226,232,240,.95);
  padding:20px; background:#fff;
}
.price-total { font-size:1.3rem; font-weight:800; }
.actions { display:flex; justify-content:flex-end; gap:10px; }
.btn-primary { border-radius:999px !important; }
.btn-soft    { border-radius:999px !important; }

/* ── Custom product card ── */
.cpc {
  display:grid;
  grid-template-columns:66px 1fr 28px;
  gap:12px;
  align-items:start;
  background:#f8fafc;
  border:1px solid #e2e8f0;
  border-radius:13px;
  padding:13px 14px;
  margin-bottom:10px;
}
.cpc-img {
  width:66px; height:66px; border-radius:10px;
  overflow:hidden; border:1px solid #e2e8f0; background:#e8eaf0;
  display:flex; align-items:center; justify-content:center;
  font-size:22px; color:#94a3b8; flex-shrink:0;
}
.cpc-img img { width:100%; height:100%; object-fit:cover; display:block; }
.cpc-body { display:flex; flex-direction:column; gap:7px; }
.cpc-sub  { display:grid; grid-template-columns:1fr 100px 72px; gap:7px; align-items:end; }
.cpc-lbl  { font-size:10.5px; font-weight:600; color:#64748b; margin-bottom:3px; display:block; }
.cpc-badge {
  font-size:11px; font-weight:700; color:#4338ca;
  background:#eef2ff; border-radius:999px;
  padding:3px 11px; display:inline-block; white-space:nowrap;
}
.cpc-del {
  background:none; border:none; padding:4px 2px; margin-top:1px;
  color:#cbd5e1; font-size:16px; cursor:pointer; line-height:1;
  transition:color .15s; align-self:start;
}
.cpc-del:hover { color:#ef4444; }
.add-item-btn {
  width:100%; margin-top:2px;
  border:1.5px dashed #a5b4fc; background:transparent; color:#6366f1;
  border-radius:12px; padding:10px;
  font-size:13px; font-weight:700; cursor:pointer;
  transition:background .15s;
  display:flex; align-items:center; justify-content:center; gap:7px;
}
.add-item-btn:hover { background:#eef2ff; }

/* ── Description ── */
.desc-hint { font-size:11px; color:#94a3b8; margin-top:5px; margin-bottom:0; }

/* ── Summary table alignment ── */
.price-box table { width:100%; table-layout:fixed; }
.price-box table td:first-child { width:60%; }
.price-box table td:last-child  { width:40%; text-align:right; font-weight:700; font-variant-numeric:tabular-nums; white-space:nowrap; }
.price-box .price-total td { font-size:1.1rem; font-weight:800; }
`;

/* ======================================================
   HELPERS
====================================================== */
function genCode() {
  return `EDPR-${Math.floor(100000000 + Math.random() * 900000000)}#`;
}
let _uid = 100;
const uid = () => ++_uid;
const lineTotal = (it) => (parseFloat(it.price) || 0) * (parseInt(it.qty) || 1);

/* ======================================================
   CustomProductCard — React.memo prevents re-mount on parent re-render
   so inputs never lose focus while the user is typing
====================================================== */
const CustomProductCard = React.memo(function CustomProductCard({ item, onChange, onRemove }) {
  const [imgErr, setImgErr] = useState(false);

  // Stable field setter — avoids recreating lambdas per keystroke
  const set = useCallback((field, val) => {
    onChange(item.id, field, val);
  }, [item.id, onChange]);

  return (
    <div className="cpc">
      {/* Thumbnail */}
      <div className="cpc-img">
        {item.imageUrl && !imgErr
          ? <img src={item.imageUrl} alt="" onError={() => setImgErr(true)} />
          : <span>🖼</span>
        }
      </div>

      {/* Fields */}
      <div className="cpc-body">
        {/* Name */}
        <div>
          <span className="cpc-lbl">Nome do Produto / Serviço</span>
          <Form.Control
            size="sm"
            placeholder="Ex: Câmera IP Hikvision"
            value={item.name}
            onChange={e => set("name", e.target.value)}
          />
        </div>

        {/* Price · Qty · Total badge */}
        <div className="cpc-sub">
          <div>
            <span className="cpc-lbl">Preço Unit. (Kz)</span>
            <Form.Control
              size="sm" type="number" min="0"
              value={item.price}
              onChange={e => set("price", e.target.value)}
            />
          </div>
          <div>
            <span className="cpc-lbl">Qtd.</span>
            <Form.Control
              size="sm" type="number" min="1"
              value={item.qty}
              onChange={e => set("qty", e.target.value)}
            />
          </div>
          <div style={{ display:"flex", alignItems:"flex-end", paddingBottom:1 }}>
            <span className="cpc-badge">{lineTotal(item).toLocaleString("pt-PT")} Kz</span>
          </div>
        </div>

        {/* Image URL */}
        <div>
          <span className="cpc-lbl">URL da Imagem (opcional)</span>
          <Form.Control
            size="sm" type="url"
            placeholder="https://..."
            value={item.imageUrl}
            onChange={e => { setImgErr(false); set("imageUrl", e.target.value); }}
          />
        </div>
      </div>

      {/* Remove button */}
      <button className="cpc-del" title="Remover" onClick={() => onRemove(item.id)}>✕</button>
    </div>
  );
});

/* ======================================================
   MAIN COMPONENT
====================================================== */
export default function SchoolProposalModal({ toggle_btn }) {

  const [show,        setShow]        = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [proposalCode,setProposalCode]= useState("");

  // Proposal meta
  const [proposalName,    setProposalName]    = useState("");
  const [clientName,      setClientName]      = useState("");
  const [institutionName, setInstitutionName] = useState("");
  const [institutionNif,  setInstitutionNif]  = useState("");
  const [proposalDesc,    setProposalDesc]    = useState(""); // ← optional description

  // Plan
  const [categoryKey, setCategoryKey] = useState("publicSchools");
  const [planId,      setPlanId]      = useState("");
  const [billing,     setBilling]     = useState("anual");
  const [students,    setStudents]    = useState(0);

  // Services
  const [smsQuantity,      setSmsQuantity]      = useState(1000);
  const [selectedServices, setSelectedServices] = useState([]);

  // Custom products — initialised once from defaults
  const [customItems, setCustomItems] = useState(() =>
    DEFAULT_CUSTOM_ITEMS.map(i => ({ ...i }))
  );

  // Discount
  const [discountPercent, setDiscountPercent] = useState(0);

  // Generate a fresh code every time the modal opens
  useEffect(() => {
    if (show) setProposalCode(genCode());
  }, [show]);

  // Derived
  const category   = EDUALL_PRICING.categories[categoryKey];
  const plan       = category?.plans.find(p => p.id === planId);
  const multiplier = BILLING_MULTIPLIER[billing];

  /* ── Stable handlers for custom items ──
     These never change identity, so React.memo on CustomProductCard
     prevents any child from re-mounting while the user is typing.        */
  const handleItemChange = useCallback((id, field, val) => {
    setCustomItems(prev =>
      prev.map(it => it.id === id ? { ...it, [field]: val } : it)
    );
  }, []);

  const handleItemRemove = useCallback((id) => {
    setCustomItems(prev => prev.filter(it => it.id !== id));
  }, []);

  const handleAddItem = () => {
    setCustomItems(prev => [...prev, { id: uid(), name:"", price:0, qty:1, imageUrl:"" }]);
  };

  // Totals
  const customTotal = useMemo(() =>
    customItems.reduce((acc, it) => acc + lineTotal(it), 0),
  [customItems]);

  const calculation = useMemo(() => {
    let base = 0;
    if (plan) {
      base = plan.billing === "anual_por_aluno"
        ? plan.price * students
        : plan.price * multiplier;
    }

    let servicesTotal = 0;
    selectedServices.forEach(id => {
      const svc = EDUALL_PRICING.additionalServices.find(s => s.id === id);
      if (!svc) return;
      switch (svc.billing) {
        case "mensal":  servicesTotal += svc.price * multiplier; break;
        case "por_sms": servicesTotal += svc.price * smsQuantity; break;
        default:        servicesTotal += svc.price; break;
      }
    });

    servicesTotal += customTotal; // custom items count as services

    const subtotal      = base + servicesTotal;
    const discountValue = (subtotal * discountPercent) / 100;
    const total         = subtotal - discountValue;

    return { base, servicesTotal, subtotal, discountValue, total };
  }, [plan, students, multiplier, selectedServices, smsQuantity, customTotal, discountPercent]);

  /* ======================================================
     PDF HTML
  ====================================================== */
  const buildPDF = () => {
    const coverUrl  = "https://ik.imagekit.io/zks5iegia/1.png";
    const footerUrl = "https://ik.imagekit.io/zks5iegia/9.png";
    const today     = new Date().toLocaleDateString("pt-PT");

    const modulesIncluded = plan?.includedModules === "todos"
      ? EDUALL_PRICING.modules
      : plan?.includedModules || [];
    const modulesInline = modulesIncluded.map((m, i) => `${i+1} - ${m}`).join("; ");

    // Catalog service rows
    const catalogRows = selectedServices.map(id => {
      const s = EDUALL_PRICING.additionalServices.find(x => x.id === id);
      if (!s) return "";
      let value = 0;
      switch (s.billing) {
        case "mensal":  value = s.price * multiplier; break;
        case "por_sms": value = s.price * smsQuantity; break;
        default:        value = s.price; break;
      }
      return `<div class="srow"><span>${s.name}</span><span class="price">${value.toLocaleString("pt-PT")} Kz</span></div>`;
    }).join("");

    // Custom product rows
    const customRows = customItems.filter(it => it.name.trim()).map(it => {
      const total = lineTotal(it);
      const imgTag = it.imageUrl
        ? `<img src="${it.imageUrl}" style="width:46px;height:46px;object-fit:cover;border-radius:8px;border:1px solid #ddd;flex-shrink:0;" crossorigin="anonymous"/>`
        : `<span style="display:inline-block;width:46px;height:46px;background:#f1f5f9;border-radius:8px;border:1px solid #ddd;flex-shrink:0;"></span>`;
      return `
        <div class="srow srow-img">
          <span style="display:flex;align-items:center;gap:10px;">
            ${imgTag}
            <span>
              <strong>${it.name}</strong><br/>
              <small style="color:#64748b;font-size:10px;">${(parseFloat(it.price)||0).toLocaleString("pt-PT")} Kz × ${it.qty} un.</small>
            </span>
          </span>
          <span class="price">${total.toLocaleString("pt-PT")} Kz</span>
        </div>`;
    }).join("");

    const hasCatalog    = selectedServices.length > 0;
    const hasCustom     = customItems.filter(it => it.name.trim()).length > 0;
    const hasPlan       = !!plan;
    const hasModules    = modulesIncluded.length > 0 && hasPlan;
    // Title logic: if no catalog services → only custom products → different heading
    const servicesTitle = hasCatalog
      ? "Serviços e Produtos Adicionais"
      : "Produtos Adicionais";

    // Optional description block
    const descBlock = proposalDesc.trim()
      ? `<div class="section">
           <div class="section-title">Notas e Observações</div>
           <div class="desc-text">${proposalDesc.trim().replace(/\n/g, "<br/>")}</div>
         </div>`
      : "";

    return `
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');
      *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
      body { font-family:'Montserrat',sans-serif; font-size:11.5px; color:#000; }
      #pdf-root { width:794px; }

      .cover {
        width:794px; height:1123px;
        background:url('${coverUrl}') center/cover no-repeat;
        display:block;
      }
      @page { margin:25mm 22mm 25mm 22mm; }
      .content { width:794px; padding:54px 74px; }

      .title        { font-size:22px; font-weight:700; color:#5b4dff; margin-bottom:10px; }
      .proposal-date{ font-size:11.5px; font-weight:600; color:#444; margin-bottom:24px; }

      .section { margin-top:24px; page-break-inside:avoid; break-inside:avoid; }
      .section-title {
        font-size:13px; font-weight:700; color:#5b4dff; margin-bottom:11px;
        border-bottom:2px solid #eef0ff; padding-bottom:5px;
      }

      .grid { display:grid; grid-template-columns:1fr 1fr; gap:15px 40px; }
      .lbl  { font-size:9.5px; font-weight:700; color:#888; display:block;
               text-transform:uppercase; letter-spacing:.5px; margin-bottom:2px; }
      .val  { font-size:11.5px; font-weight:600; color:#1a1d2e; }

      /* Plan box */
      .plan-box {
        padding:14px 16px; background:#f5f6ff;
        border-left:4px solid #5b4dff; border-radius:6px;
        font-size:12px; font-weight:700; color:#1a1d2e;
      }
      .plan-box .plan-name { font-size:13px; font-weight:700; color:#5b4dff; }
      .plan-detail {
        display:flex; justify-content:space-between; align-items:center;
        margin-top:8px; padding-top:8px; border-top:1px solid #dde0ff;
        font-size:11px;
      }
      .plan-detail .pd-label { font-weight:600; color:#555; }
      .plan-detail .pd-value { font-weight:800; color:#1a1d2e; font-size:12px; }
      .plan-detail .pd-value.big { font-size:13px; color:#5b4dff; }

      .modules-inline { font-size:11px; font-weight:600; line-height:1.9; color:#334155; }

      .srow {
        display:flex; justify-content:space-between; align-items:center;
        padding:9px 0; border-bottom:1px solid #eee;
        font-size:11px; font-weight:600;
      }
      .srow:last-child { border-bottom:none; }
      .srow-img { min-height:54px; }
      .price { font-weight:800; color:#1a1d2e; }

      .sub-lbl {
        font-size:9.5px; font-weight:700; color:#5b4dff; margin:12px 0 5px;
        text-transform:uppercase; letter-spacing:.5px;
      }

      /* Summary — fixed two-column table */
      .sum-table { width:100%; border-collapse:collapse; table-layout:fixed; }
      .sum-table td {
        padding:8px 0; border-bottom:1px solid #eee;
        font-size:11.5px; font-weight:600; color:#334155;
      }
      .sum-table td:last-child {
        text-align:right; width:180px;
        font-variant-numeric:tabular-nums; white-space:nowrap;
      }
      .sum-table tr:last-child td { border-bottom:none; }
      .sum-table .red td { color:#ef4444; }
      .sum-table .total-row td {
        background:#5b4dff; color:#fff; padding:11px 14px;
        font-size:13.5px; font-weight:800; border:none; border-radius:0;
      }
      .sum-table .total-row { border-radius:6px; overflow:hidden; }
      .sum-table .total-row td:first-child { border-radius:6px 0 0 6px; padding-left:14px; }
      .sum-table .total-row td:last-child  { border-radius:0 6px 6px 0; padding-right:14px; }

      .desc-text {
        font-size:11px; font-weight:500; color:#334155; line-height:1.8;
        background:#f8fafc; border-left:3px solid #c7d2fe;
        border-radius:6px; padding:12px 14px;
      }

      .footer {
        width:794px; height:1123px;
        background:url('${footerUrl}') center/cover no-repeat;
        page-break-before:always; break-inside:avoid;
        display:block; overflow:hidden;
      }
    </style>

    <div id="pdf-root">
      <div class="cover"></div>

      <div class="content">
        <div class="title">Proposta Comercial</div>
        <div class="proposal-date">Data da Proposta: <strong>${today}</strong></div>

        <!-- Identificação -->
        <div class="section">
          <div class="section-title">Identificação</div>
          <div class="grid">
            <div><span class="lbl">Código</span>     <span class="val">${proposalCode}</span></div>
            <div><span class="lbl">Proposta</span>   <span class="val">${proposalName}</span></div>
            <div><span class="lbl">Cliente</span>    <span class="val">${clientName}</span></div>
            <div><span class="lbl">Instituição</span><span class="val">${institutionName}</span></div>
            <div><span class="lbl">NIF</span>        <span class="val">${institutionNif}</span></div>
            <div><span class="lbl">Categoria</span>  <span class="val">${category?.name}</span></div>
          </div>
        </div>

        <!-- Plano (apenas se selecionado) -->
        ${hasPlan ? `
        <div class="section">
          <div class="section-title">Plano Selecionado</div>
          <div class="plan-box">
            <div class="plan-name">${plan.name} <span style="font-weight:500;color:#666;font-size:11px;">— ${billing}</span></div>
            ${plan.billing === "anual_por_aluno" ? `
              <div class="plan-detail">
                <span class="pd-label">Valor por estudante</span>
                <span class="pd-value">${plan.price.toLocaleString("pt-PT")} Kz</span>
              </div>
              <div class="plan-detail">
                <span class="pd-label">Nº de estudantes</span>
                <span class="pd-value">${students}</span>
              </div>
              <div class="plan-detail">
                <span class="pd-label">Total do Plano</span>
                <span class="pd-value big">${plan.price.toLocaleString("pt-PT")} Kz × ${students} = ${calculation.base.toLocaleString("pt-PT")} Kz</span>
              </div>
            ` : `
              <div class="plan-detail">
                <span class="pd-label">Valor ${billing}</span>
                <span class="pd-value big">${calculation.base.toLocaleString("pt-PT")} Kz</span>
              </div>
            `}
          </div>
        </div>` : ""}

        <!-- Módulos (apenas se há plano com módulos) -->
        ${hasModules ? `
        <div class="section">
          <div class="section-title">Módulos Inclusos</div>
          <div class="modules-inline">${modulesInline}</div>
        </div>` : ""}

        <!-- Serviços + Produtos personalizados -->
        ${hasCatalog || hasCustom ? `
        <div class="section">
          <div class="section-title">${servicesTitle}</div>
          ${catalogRows}
          ${hasCustom ? `
            ${hasCatalog ? `<div class="sub-lbl">Produtos Personalizados</div>` : ""}
            ${customRows}
          ` : ""}
        </div>` : ""}

        <!-- Resumo financeiro -->
        <div class="section">
          <div class="section-title">Resumo Financeiro</div>
          <table class="sum-table">
            <tbody>
              ${hasPlan ? (plan.billing === "anual_por_aluno"
                ? `<tr><td>Plano (${plan.price.toLocaleString("pt-PT")} Kz × ${students} estudantes)</td><td>${calculation.base.toLocaleString("pt-PT")} Kz</td></tr>`
                : `<tr><td>Licenciamento (${billing})</td><td>${calculation.base.toLocaleString("pt-PT")} Kz</td></tr>`
              ) : ""}
              ${hasCatalog || hasCustom
                ? `<tr><td>${servicesTitle}</td><td>${calculation.servicesTotal.toLocaleString("pt-PT")} Kz</td></tr>`
                : ""}
              <tr><td>Subtotal</td><td>${calculation.subtotal.toLocaleString("pt-PT")} Kz</td></tr>
              <tr class="red"><td>Desconto (${discountPercent}%)</td><td>− ${calculation.discountValue.toLocaleString("pt-PT")} Kz</td></tr>
              <tr class="total-row"><td>Total Geral</td><td>${calculation.total.toLocaleString("pt-PT")} Kz</td></tr>
            </tbody>
          </table>
        </div>

        <!-- Notas / Observações (opcional) -->
        ${descBlock}
      </div>

      <div class="footer"></div>
    </div>`;
  };

  /* ======================================================
     EXPORT PDF
  ====================================================== */
  const exportPDF = async () => {
    if (!proposalName || !clientName || !institutionName) {
      toast.error("Preencha os dados principais.");
      return;
    }
    try {
      setLoading(true);
      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-9999px;top:0;";
      container.innerHTML = buildPDF();
      document.body.appendChild(container);

      await html2pdf()
        .from(document.getElementById("pdf-root"))
        .set({
          margin: 0,
          filename: `${proposalCode}.pdf`,
          html2canvas: { scale:2, useCORS:true, allowTaint:true },
          jsPDF: { unit:"mm", format:"a4" },
          pagebreak: { mode:["css"] }
        })
        .save();

      document.body.removeChild(container);
      toast.success("PDF gerado com sucesso!");
    } catch {
      toast.error("Erro ao gerar PDF");
    } finally {
      setLoading(false);
    }
  };

  /* ======================================================
     RENDER
  ====================================================== */
  return (
    <>
      <style>{styles}</style>

      <div onClick={() => setShow(true)} style={{ display:"inline-block" }}>
        {toggle_btn || <Button>Nova Proposta</Button>}
      </div>

      <Modal show={show} onHide={() => !loading && setShow(false)} centered className="pretty-modal">
        <Modal.Header closeButton={!loading}>
          <Modal.Title>Calculadora Comercial EDUALL</Modal.Title>
        </Modal.Header>

        <Modal.Body>

          {/* ── DADOS DA PROPOSTA ── */}
          <div className="cardlet">
            <div className="cardlet-title">Dados da Proposta</div>
            <Form.Control className="mb-2" value={proposalCode} disabled />
            <Form.Control className="mb-2" placeholder="Nome da Proposta"    value={proposalName}    onChange={e => setProposalName(e.target.value)} />
            <Form.Control className="mb-2" placeholder="Nome do Cliente"     value={clientName}      onChange={e => setClientName(e.target.value)} />
            <Form.Control className="mb-2" placeholder="Nome da Instituição" value={institutionName} onChange={e => setInstitutionName(e.target.value)} />
            <Form.Control              placeholder="NIF da Instituição"  value={institutionNif}  onChange={e => setInstitutionNif(e.target.value)} />
          </div>

          {/* ── PLANO E FATURAÇÃO ── */}
          <div className="cardlet">
            <div className="cardlet-title">Plano e Faturação</div>
            <Form.Select
              value={categoryKey}
              onChange={e => { setCategoryKey(e.target.value); setPlanId(""); }}
              className="mb-2"
            >
              {Object.keys(EDUALL_PRICING.categories).map(key => (
                <option key={key} value={key}>{EDUALL_PRICING.categories[key].name}</option>
              ))}
            </Form.Select>
            <Form.Select value={planId} onChange={e => setPlanId(e.target.value)} className="mb-2">
              <option value="">Selecionar Plano</option>
              {category?.plans.map(p => (
                <option key={p.id} value={p.id}>{p.name} — {p.price.toLocaleString()} Kz</option>
              ))}
            </Form.Select>
            <Form.Select value={billing} onChange={e => setBilling(e.target.value)}>
              <option value="trimestral">Trimestral</option>
              <option value="semestral">Semestral</option>
              <option value="anual">Anual</option>
            </Form.Select>
          </div>

          {plan?.billing === "anual_por_aluno" && (
            <div className="cardlet">
              <div className="cardlet-title">Número de Estudantes</div>
              <Form.Control
                type="number" value={students}
                onChange={e => setStudents(Number(e.target.value))}
              />
            </div>
          )}

          {/* ── SERVIÇOS DO CATÁLOGO ── */}
          <div className="cardlet">
            <div className="cardlet-title">Serviços Adicionais</div>
            {EDUALL_PRICING.additionalServices.map(svc => (
              <div key={svc.id} className="mb-2">
                <Form.Check
                  type="switch"
                  label={`${svc.name} — ${svc.price.toLocaleString()} Kz`}
                  checked={selectedServices.includes(svc.id)}
                  onChange={e => {
                    if (e.target.checked) setSelectedServices(p => [...p, svc.id]);
                    else setSelectedServices(p => p.filter(id => id !== svc.id));
                  }}
                />
                {svc.billing === "por_sms" && selectedServices.includes(svc.id) && (
                  <Form.Control
                    type="number" className="mt-1"
                    value={smsQuantity}
                    onChange={e => setSmsQuantity(Number(e.target.value))}
                  />
                )}
              </div>
            ))}
          </div>

          {/* ── PRODUTOS / SERVIÇOS PERSONALIZADOS ── */}
          <div className="cardlet">
            <div className="cardlet-title">Produtos / Serviços Personalizados</div>

            {customItems.map(item => (
              <CustomProductCard
                key={item.id}
                item={item}
                onChange={handleItemChange}
                onRemove={handleItemRemove}
              />
            ))}

            <button className="add-item-btn" onClick={handleAddItem}>
              + Adicionar Produto / Serviço
            </button>
          </div>

          {/* ── DESCONTO ── */}
          <div className="cardlet">
            <div className="cardlet-title">Desconto Comercial (%)</div>
            <Form.Control
              type="number" value={discountPercent}
              onChange={e => setDiscountPercent(Number(e.target.value))}
            />
          </div>

          {/* ── NOTAS / OBSERVAÇÕES (opcional) ── */}
          <div className="cardlet">
            <div className="cardlet-title">Notas / Observações <span style={{ fontWeight:400, color:"#94a3b8" }}>(opcional)</span></div>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Ex: Esta proposta é válida por 30 dias. Inclui instalação e formação inicial para até 5 utilizadores..."
              value={proposalDesc}
              onChange={e => setProposalDesc(e.target.value)}
              style={{ resize:"vertical", fontSize:"13px" }}
            />
            <p className="desc-hint">Se preenchido, aparecerá como secção final da proposta em PDF.</p>
          </div>

          {/* ── RESUMO FINANCEIRO ── */}
          <div className="price-box">
            <Table bordered>
              <colgroup>
                <col style={{ width:"60%" }}/>
                <col style={{ width:"40%" }}/>
              </colgroup>
              <tbody>
                <tr>
                  <td>Licenciamento</td>
                  <td style={{ textAlign:"right", whiteSpace:"nowrap" }}>{calculation.base.toLocaleString("pt-PT")} Kz</td>
                </tr>
                <tr>
                  <td>Serviços / Produtos</td>
                  <td style={{ textAlign:"right", whiteSpace:"nowrap" }}>{calculation.servicesTotal.toLocaleString("pt-PT")} Kz</td>
                </tr>
                <tr>
                  <td>Subtotal</td>
                  <td style={{ textAlign:"right", whiteSpace:"nowrap" }}>{calculation.subtotal.toLocaleString("pt-PT")} Kz</td>
                </tr>
                <tr style={{ color:"#ef4444" }}>
                  <td>Desconto ({discountPercent}%)</td>
                  <td style={{ textAlign:"right", whiteSpace:"nowrap" }}>- {calculation.discountValue.toLocaleString("pt-PT")} Kz</td>
                </tr>
                <tr className="price-total">
                  <td>Total Geral</td>
                  <td style={{ textAlign:"right", whiteSpace:"nowrap" }}>{calculation.total.toLocaleString("pt-PT")} Kz</td>
                </tr>
              </tbody>
            </Table>
          </div>

        </Modal.Body>

        <Modal.Footer>
          <div className="actions" style={{ width:"100%" }}>
            <Button
              variant="outline-secondary" className="btn-soft"
              disabled={loading} onClick={() => setShow(false)}
            >
              Fechar
            </Button>
            <Button variant="primary" disabled={loading} onClick={exportPDF}>
              {loading ? <Spinner size="sm" animation="border"/> : "Gerar PDF"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}